import { auth } from '../firebase';

const API = '/api';

/**
 * Get available slots from backend — fully real-time accurate
 * Uses: booking status, staff count, avg service time, current time
 */
export const getAvailableSlots = async (salonId, date, openingTime, closingTime) => {
  console.log('📍 getAvailableSlots:', { salonId, date });

  try {
    // Fetch from backend — most accurate source
    const res = await fetch(`${API}/salon/${salonId}/slots?date=${date}`);
    if (res.ok) {
      const data = await res.json();
      const slots = data.available_slots || [];

      // Filter past slots for today
      const todayStr = new Date().toISOString().split('T')[0];
      const isToday = date === todayStr;
      const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
      const BUFFER = 30; // 30 min buffer

      const result = slots.map(slot => {
        let isPast = false;
        if (isToday) {
          const [h, m] = slot.time.split(':').map(Number);
          isPast = (h * 60 + m) < (nowMinutes + BUFFER);
        }
        return {
          time: slot.time,
          isAvailable: slot.available > 0 && !isPast,
          available: slot.available,
          total: slot.total,
          booked: slot.booked,
          isPast,
        };
      });

      console.log('✅ Slots from API:', result.length, 'available:', result.filter(s => s.isAvailable).length);
      return result;
    }
  } catch (err) {
    console.warn('⚠️ Backend slots failed, using fallback:', err.message);
  }

  // Fallback — generate from opening/closing time if API fails
  return generateFallbackSlots(openingTime, closingTime, date);
};

/**
 * Generate slots locally as fallback
 */
const generateFallbackSlots = (openingTime = '09:00', closingTime = '20:00', date, slotDuration = 30) => {
  const slots = [];
  const [openH, openM] = openingTime.split(':').map(Number);
  const [closeH, closeM] = closingTime.split(':').map(Number);
  let current = openH * 60 + openM;
  const close = closeH * 60 + closeM;

  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = date === todayStr;
  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
  const BUFFER = 30;

  while (current < close) {
    const h = Math.floor(current / 60);
    const m = current % 60;
    const time = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
    const isPast = isToday && (current < nowMinutes + BUFFER);
    slots.push({ time, isAvailable: !isPast, available: isPast ? 0 : 1, total: 1, booked: 0, isPast });
    current += slotDuration;
  }
  return slots;
};

/**
 * Book a slot — creates booking via API with auth token
 */
export const bookSlotWithTransaction = async (salonId, date, time, bookingData) => {
  console.log('🎫 Booking started:', { salonId, date, time });

  if (!auth.currentUser) return { success: false, error: 'Please login to book' };

  try {
    const token = await auth.currentUser.getIdToken(true);
    const res = await fetch(`${API}/booking/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...bookingData,
        salon_id: salonId,
        booking_date: date,
        slot_time: time,
      }),
    });

    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error || 'Booking failed' };

    console.log('✅ Booking created:', data.booking_id);
    return { success: true, bookingId: data.booking_id };
  } catch (err) {
    console.error('❌ Booking error:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Release a slot lock (on cancel)
 */
export const releaseSlot = async (salonId, date, time) => {
  try {
    const token = await auth.currentUser?.getIdToken() || '';
    await fetch(`${API}/slot/release`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ salon_id: salonId, date, slot_time: time }),
    });
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Generate slots (used by SalonDashboard preview)
 */
export const generateSlots = (openingTime, closingTime, slotDuration = 30) => {
  const slots = [];
  const [openH, openM] = openingTime.split(':').map(Number);
  const [closeH, closeM] = closingTime.split(':').map(Number);
  let current = openH * 60 + openM;
  const close = closeH * 60 + closeM;
  while (current < close) {
    const h = Math.floor(current / 60);
    const m = current % 60;
    slots.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
    current += slotDuration;
  }
  return slots;
};
